import React from 'react';
import { Field, FieldArray } from 'redux-form';
import moment from 'moment';
import ReactMarkdown from 'react-markdown';
import { required, length, date } from 'redux-form-validators';
import { Col, Row } from 'react-bootstrap';
import NameFields from '../../NameFields/NameFields';
import {
  DatePickerDob2,
  HorizontalFormControl,
} from '../../../../../../../common/components';
import { CheckboxGroup } from '../../../../../../../common/components/ReduxFormField';
import InsuredAddress from '../../InsuredAddress/InsuredAddress';
import DirectDebitDeclarations from './Components/DirectDebitDeclarations/DirectDebitDeclarations.';
import Marketing from './Components/Marketing/Marketing';
const cleanNumber = (value) => {
  return value ? value.replace(/[^0-9]+/g, '') : '';
};

const getChildAgeLimit = (maxAge) => {
  // SC child age limit is 22, but add 1 as it is inclusive,
  // i.e. 22 years 11 months 30 days etc.

  return moment()
    .subtract(maxAge, 'year')
    .toDate();
};

const Child = ({ fields, rules }) => {
  if (fields.length === 0) {
    return null;
  }

  const childInEducationMaxAge = rules.child_in_education_max_age;
  const childMaxAge = rules.child_max_age;
  const maxAge = childInEducationMaxAge ? childInEducationMaxAge : childMaxAge;

  const fullTimeEducationMessage = childInEducationMaxAge
    ? `or up to age ${childInEducationMaxAge} if in full time education`
    : '';
  const message = `DOB is invalid. Any children to be covered must be under ${childMaxAge} years of age ${fullTimeEducationMessage}`;

  return fields.map((field) => (
    <div className={'pa-form-section'}>
      <h2>Children Details</h2>

      <NameFields
        baseName={field}
        label="Name"
        labelSize={4}
        fieldSize={6}
        child={false}
      />
      <Field
        name={`${field}.dob`}
        showYear
        component={DatePickerDob2}
        label="Date of birth"
        labelSize={4}
        componentSize={6}
        validate={[
          date({
            format: 'dd/mm/yyyy',
            '>': getChildAgeLimit(maxAge),
            msg: message,
          }),
        ]}
      />
    </div>
  ));
};

const PayTab = ({
  purchaseDeclaration,
  paymentDeclarations,
  finalPremium,
  issuingError,
  productCode,
  rules,
}) => {
  const { gross } = finalPremium.attributes.premium;
  const monthlyGross = gross ? gross : 0;
  let paymentDeclarationsSelected = false;

  if (paymentDeclarations[0].answer && paymentDeclarations[1].answer) {
    paymentDeclarationsSelected = true;
  }

  return (
    <div className={'pa-tab-content'}>
      <FieldArray
        name={'data.attributes.metadata.children'}
        productCode={productCode}
        component={Child}
        rules={rules}
      />
      <h3>Declaration</h3>
      <hr />
      <div className={'declaration-content'}>
        <ReactMarkdown source={purchaseDeclaration} />
      </div>
      <Row style={{ marginBottom: '30px' }}>
        <Col sm={12}>
          <Field
            name={'meta.purchase_declaration'}
            inline
            options={[
              {
                label: 'Please tick to confirm you agree to the above declaration',
                value: true,
              },
            ]}
            validate={required({ msg: 'Please confirm the above declaration' })}
            component={CheckboxGroup}
          />
        </Col>
      </Row>
      {gross > 0 && (
        <>
          <h3>Payment Details</h3>
          <hr />
          <Row>
            <Col sm={7}>
              <p>Monthly Payment Plan: 12 payments of £{monthlyGross.toFixed(2)}</p>
            </Col>
          </Row>
          <Row>
            <Col sm={12}>
              <p>
                We need your bank details to set up a new policy. Please note your cover
                will not commence if you do not submit a valid Direct Debit Instruction.
                We confirm all the normal safeguards of the Direct Debit Scheme apply and
                we will confirm the details of your instructions in writing. Alternative
                methods of payment cannot be accepted.
              </p>
            </Col>
          </Row>
          <Row>
            <Col sm={12}>
              <p>
                Please confirm the following two questions before completing the direct
                debit details.
                <b>
                  {' '}
                  If you cannot answer yes to both questions your application will not be
                  submitted and we will be unable to issue you a policy.
                </b>
              </p>
            </Col>
          </Row>
          <Row>
            <Col sm={12}>
              <FieldArray
                name={'data.attributes.metadata.direct_debit_payment_declaration_answers'}
                component={DirectDebitDeclarations}
                validate={required({ msg: 'Account holder name is required' })}
              />
            </Col>
          </Row>
          {paymentDeclarationsSelected && (
            <>
              <Row>
                <Col sm={12}>
                  <p>
                    *Remember: you must take reasonable care to give us full and accurate
                    answers. If not, we may cancel or change your policy or refuse to pay
                    all or part of a claim
                  </p>
                </Col>
              </Row>
              <Row>
                <Col sm={9}>
                  <Field
                    name={'meta.account_holder'}
                    labelSize={4}
                    fieldSize={7}
                    label="Account Holder Name"
                    component={HorizontalFormControl}
                    validate={required({ msg: 'Account holder name is required' })}
                  />
                  <Field
                    name={'meta.account_number'}
                    labelSize={4}
                    fieldSize={7}
                    parse={cleanNumber}
                    label="Account Number"
                    component={HorizontalFormControl}
                    validate={[
                      required({ msg: 'Account number is required' }),
                      length({
                        msg: 'Account Number is invalid',
                        in: [6, 12],
                      }),
                    ]}
                  />
                  <Field
                    name={'meta.sort_code'}
                    labelSize={4}
                    fieldSize={3}
                    label="Sort code"
                    parse={cleanNumber}
                    component={HorizontalFormControl}
                    validate={[
                      required({ msg: 'Sort code is required' }),
                      length({
                        msg: 'Sort code is invalid',
                        is: 6,
                      }),
                    ]}
                  />
                  <Field
                    name={'data.attributes.metadata.adults.0'}
                    component={InsuredAddress}
                  />
                </Col>
              </Row>
              <Marketing />
            </>
          )}
        </>
      )}
      {issuingError && <h2>{`Error Issuing: ${issuingError} - unable to issue`}</h2>}
    </div>
  );
};

export default PayTab;
